/**
 * 🎓 Premium User Dashboard - RATIONALISÉ
 * =========================================
 * 
 * Structure des onglets:
 * - Mon compte: Infos personnelles, abonnement, mot de passe (AUCUNE donnée pédagogique)
 * - Pédagogie: UNIQUE source de vérité pour TOUTE progression pédagogique (Owen)
 * - Dashboard Affilié: (si affilié)
 * 
 * NOTE: L'onglet "Mon Parcours" supprimé (désactivation Pinecone).
 * Le parcours est visible en aperçu sur le Tableau de Bord Principal.
 * 
 * RÈGLE FONDAMENTALE:
 * - Aucune donnée de progression dans ""
 * - "Pédagogie" = seul endroit pour voir la progression
 */

import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { 
  User, 
  CreditCard, 
  Lock, 
  Users, 
  Calendar,
  ArrowLeft,
  Info,
  GraduationCap,
  Settings,
  Building2,
  ArrowUpDown,
  TrendingDown,
  TrendingUp,
  Loader2
} from "lucide-react";
import { Receipt as ReceiptIcon } from "lucide-react";
import useSafeBackNavigation from "../hooks/useSafeBackNavigation";

// Import components
// MonParcoursInsights removed - tab supprimé suite désactivation Pinecone
import AffiliateDashboardAwwwards from "./AffiliateDashboardAwwwards";

// 🎓 Import Gouvernance Pédagogique (Qualiopi) - UNIQUE source de progression
import PedagogicalSummary from "../components/PedagogicalSummary";

// 🏢 Import Gestion Licences Entreprise
import EnterpriseLicenseTab from "../components/enterprise/EnterpriseLicenseTab";

// 🧾 Onglet Factures (Phase 2 — AMSY E-COM / COACH DIA)
import InvoicesTab from "../components/billing/InvoicesTab";

// 🔒 Onglet Mon contexte partagé (Owen → Coach humain)
import MyContextSharesTab from "../components/coaching/MyContextSharesTab";
import { Shield } from "lucide-react";

// 🧭 Aide & Navigation — premium help modals (desktop + mobile)
import { HelpCircle } from "lucide-react";
import HelpDesktopModal from "../components/help/HelpDesktopModal";
import HelpMobileSheet from "../components/help/HelpMobileSheet";

// 🎫 Import Sessions Premium Widget (Phase 2 MOA)
import { SessionsWidget } from "../components/premium/SessionsWidget";
import { SessionsExhaustedModal } from "../components/premium/SessionsExhaustedModal";
import { SessionsLowBanner, useSessionAlerts } from "../components/premium/SessionAlerts";
import { useSessionCredits } from "../hooks/useSessionCredits";

// Import safeFetch for robust API calls
import { safeFetch } from "../utils/api";
import { safeFetchJSON } from "../utils/apiHelpers";

import { getBackendUrl } from '../config/environment';
const BACKEND_URL = getBackendUrl();

export default function UserDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [passwords, setPasswords] = useState({ old: "", new: "" });
  const [loadingPassword, setLoadingPassword] = useState(false);
  const [loadingCancel, setLoadingCancel] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  
  // 🏢 État pour les entreprises (admin entreprise)
  const [userCompanies, setUserCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  
  // 🎫 Sessions Premium (Phase 2 MOA)
  const [showRechargeModal, setShowRechargeModal] = useState(false);
  const sessionCredits = useSessionCredits();
  const { showAlertIfNeeded } = useSessionAlerts();
  
  // P1 MOA: États pour la modale de sélection de pack
  const [showPackModal, setShowPackModal] = useState(false);
  // 🧭 Aide & Navigation
  const [helpOpen, setHelpOpen] = useState(false);
  const [loadingPlanChange, setLoadingPlanChange] = useState(false);
  
  // Configuration des packs avec prix (synchronisé avec backend)
  const PACKS_CONFIG = [
    { id: 'pack_decouverte', name: 'Découverte', sessions: 30, price: 5.90, popular: false },
    { id: 'pack_standard', name: 'Standard', sessions: 73, price: 12.90, popular: true },
    { id: 'pack_pro', name: 'Pro', sessions: 150, price: 22.90, popular: false }
  ];
  
  // Afficher l'alerte soft_20 si nécessaire (une fois par session)
  useEffect(() => {
    if (sessionCredits.alertLevel && sessionCredits.current > 0) {
      showAlertIfNeeded(sessionCredits.alertLevel, sessionCredits.current);
    }
  }, [sessionCredits.alertLevel, sessionCredits.current, showAlertIfNeeded]);
  
  // P1: Charger les options de changement de pack
  // ═══════════════════════════════════════════════════════════════════════════
  // P1 MOA: SÉLECTION DE PACK → STRIPE CHECKOUT
  // ═══════════════════════════════════════════════════════════════════════════
  
  // P1: Rediriger vers Stripe pour le pack sélectionné
  const handleSelectPack = async (packId) => {
    setLoadingPlanChange(true);
    try {
      const token = localStorage.getItem('token');
      const response = await safeFetchJSON(`${BACKEND_URL}/api/billing/create-upgrade-checkout`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          new_pack_id: packId,
          origin_url: window.location.origin 
        })
      });
      
      if (response.checkout_url) {
        window.location.href = response.checkout_url;
      } else {
        throw new Error('URL de paiement non reçue');
      }
    } catch (error) {
      toast.error(error.message || 'Erreur lors du changement de pack');
      setLoadingPlanChange(false);
      setShowPackModal(false);
    }
  };
  
  // P1 MOA FINAL: Détecter le retour de Stripe Checkout (mode="payment")
  // P2 MOA: Détecter le retour du Stripe Billing Portal (annulation)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const packChanged = params.get('pack_changed');
    const newPack = params.get('pack');
    const portalClosed = params.get('portal');
    
    if (packChanged === 'success') {
      // ✅ Paiement réussi - le webhook a appliqué les crédits
      toast.success(`✅ Paiement confirmé ! Vos crédits ont été ajoutés.`);
      fetchDashboardData();
      sessionCredits.refresh();
      navigate('/user-dashboard?tab=compte', { replace: true });
    } else if (packChanged === 'cancelled') {
      toast.info('Paiement annulé. Votre pack reste inchangé.');
      navigate('/user-dashboard?tab=compte', { replace: true });
    } else if (portalClosed === 'closed') {
      // 🔒 P2 MOA: Retour du Stripe Billing Portal
      // L'utilisateur peut avoir annulé, modifié ou simplement fermé le portail
      // On rafraîchit les données pour refléter l'état actuel
      toast.info('Portail Stripe fermé. Vos modifications ont été prises en compte.');
      fetchDashboardData();
      sessionCredits.refresh();
      navigate('/user-dashboard?tab=compte', { replace: true });
    }
  }, [location.search]);
  
  // Get tab from URL query params - default to 'compte' (Mon compte)
  const queryParams = new URLSearchParams(location.search);
  const rawTab = queryParams.get('tab') || 'compte';
  // Mon Parcours tab removed - redirect to compte
  const initialTab = rawTab === 'mon-parcours' ? 'compte' : rawTab;
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Update active tab when URL changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabFromUrl = params.get('tab');
    if (tabFromUrl) {
      const safeTab = tabFromUrl === 'mon-parcours' ? 'compte' : tabFromUrl;
      if (safeTab !== activeTab) {
        setActiveTab(safeTab);
      }
    }
  }, [location.search, activeTab]);
  
  const handleBack = useSafeBackNavigation('/training');
  
  // 🏢 Charger les entreprises dont l'utilisateur est admin
  const fetchUserCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const token = localStorage.getItem('token');
      const response = await safeFetchJSON(`${BACKEND_URL}/api/company/my-companies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      setUserCompanies(response.companies || []);
    } catch (error) {
      // Pas d'erreur si l'utilisateur n'est pas admin entreprise
      console.log('No enterprise access:', error);
      setUserCompanies([]);
    } finally {
      setLoadingCompanies(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchUserCompanies(); // 🏢 Charger les entreprises au démarrage
  }, []);

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found");
      }
      
      const res = await safeFetch(`${BACKEND_URL}/api/dashboard/me`, {
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      
      if (!res.ok) {
        throw new Error("Failed to fetch dashboard data");
      }
      
      const dashboardData = await res.json();
      setData(dashboardData);
    } catch (error) {
      toast.error("Erreur de chargement du Dashboard");
    } finally {
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // 🔒 P2 MOA (Jan 2026): ANNULATION VIA STRIPE PORTAL UNIQUEMENT
  // ❌ AUCUN appel backend d'annulation
  // ✅ Ouverture directe Stripe Billing Portal
  // ═══════════════════════════════════════════════════════════════════════════
  const handleCancelSubscription = async () => {
    setLoadingCancel(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found");
      }
      
      // 🔒 P2: Ouvrir Stripe Billing Portal (même endpoint que pour les upgrades)
      // L'utilisateur confirme l'annulation DANS Stripe, pas localement
      const res = await safeFetch(`${BACKEND_URL}/api/billing/open-billing-portal`, { 
        method: "POST",
        headers: {
          "Authorization": `Bearer ${token}`,
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          origin_url: window.location.origin
        })
      });
      
      const result = await res.json();
      
      if (result.portal_url) {
        // 🔒 P2: Redirection vers Stripe Portal pour annulation
        // Stripe gère la confirmation et émet le webhook
        window.location.href = result.portal_url;
      } else {
        throw new Error("URL du portail Stripe non reçue");
      }
      
    } catch (error) {
      toast.error(error.message || "Erreur lors de l'ouverture du portail Stripe");
      setLoadingCancel(false);
    }
    // Note: pas de finally car redirection vers Stripe
  };

  const handlePasswordChange = async () => {
    if (!passwords.old || !passwords.new) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }

    if (passwords.new.length < 6) {
      toast.error("Le nouveau mot de passe doit contenir au moins 6 caractères");
      return;
    }

    setLoadingPassword(true);
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("No authentication token found");
      }
      
      const res = await safeFetch(`${BACKEND_URL}/api/dashboard/change-password`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify({ 
          old_password: passwords.old, 
          new_password: passwords.new 
        })
      });
      
      const result = await res.json();
      
      if (!res.ok) {
        throw new Error(result.detail || "Erreur lors du changement de mot de passe");
      }
      
      toast.success(result.message);
      setPasswords({ old: "", new: "" });
    } catch (error) {
      toast.error(error.message || "Erreur lors du changement de mot de passe");
    } finally {
      setLoadingPassword(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-green-600';
      case 'trialing':
        return 'text-blue-600';
      case 'canceled_pending':
        return 'text-orange-600';
      case 'canceled':
        return 'text-red-600';
      case 'inactive':
      default:
        return 'text-gray-500';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active':
        return 'Abonnement actif';
      case 'trialing':
        return 'Essai gratuit en cours';
      case 'canceled_pending':
        return 'Annulation programmée';
      case 'canceled':
        return 'Abonnement arrêté — accès expiré';
      case 'inactive':
        return 'Gratuit';
      default:
        return 'Inactif';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="text-center"
        >
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-purple-600 border-r-transparent mb-4"></div>
          <p className="text-lg font-medium text-purple-700">Chargement de votre dashboard...</p>
        </motion.div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-purple-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg font-medium text-red-600">Erreur de chargement des données</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-6 py-2 bg-purple-600 text-white rounded-full hover:bg-purple-700"
          >
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 md:px-8 pb-24 md:pb-8" style={{ background: '#FAFAF8' }} data-testid="user-dashboard-page">
      {/* Bouton Retour */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        className="max-w-7xl mx-auto mb-6 flex items-center justify-between"
      >
        <Button
          onClick={handleBack}
          variant="outline"
          className="rounded-full bg-white/80 backdrop-blur-sm border-purple-200 hover:bg-purple-50 hover:border-purple-300 transition-all shadow-md whitespace-nowrap max-w-full"
        >
          <ArrowLeft className="h-4 w-4 mr-2 shrink-0" />
          <span className="sm:hidden">Retour</span>
          <span className="hidden sm:inline">Retour au dashboard</span>
        </Button>

        {/* 🧭 Aide & Navigation — premium button (desktop + mobile) */}
        <button
          type="button"
          onClick={() => setHelpOpen(true)}
          data-testid="dashboard-help-btn"
          aria-label="Ouvrir l'aide à la navigation"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-all hover:scale-[1.03] shadow-sm"
          style={{
            background: 'rgba(255,252,236,0.9)',
            color: '#1C1147',
            border: '1px solid rgba(212,175,55,0.45)',
          }}
        >
          <HelpCircle className="w-3.5 h-3.5" style={{ color: '#B7901F' }} />
          Aide & Navigation
        </button>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-7xl mx-auto mb-8"
      >
        <h1 className="text-4xl font-bold bg-gradient-to-r from-[#1C1147] via-[#3A2680] to-[#B7901F] bg-clip-text text-transparent" data-testid="user-dashboard-title">
          Mon Parcours
        </h1>
        <p className="text-[#5A5169] mt-2">Pilotez votre progression, vos sessions et vos prochaines étapes.</p>

        {/* CoachDIA Discovery — bannière premium light Iter314 */}
        <div
          data-testid="dashboard-discovery-banner"
          className="mt-5 relative overflow-hidden rounded-3xl p-6 sm:p-7 flex flex-wrap items-center gap-5"
          style={{
            background: 'linear-gradient(135deg, #FFFCEC 0%, #FFFFFF 55%, #F7F2E2 100%)',
            border: '1.5px solid rgba(212,175,55,0.40)',
            boxShadow: '0 1px 0 rgba(255,255,255,0.7) inset, 0 24px 60px -32px rgba(28,17,71,0.30)',
          }}
        >
          <span
            aria-hidden
            className="absolute -top-20 -right-16 w-64 h-64 rounded-full pointer-events-none"
            style={{
              background: 'radial-gradient(circle, rgba(212,175,55,0.25) 0%, rgba(212,175,55,0) 70%)',
              filter: 'blur(20px)',
            }}
          />
          <div className="flex-1 min-w-[260px] relative">
            <div className="flex items-center gap-2 mb-2">
              <span
                className="w-6 h-px"
                style={{ background: 'linear-gradient(90deg, transparent, #D4AF37)' }}
              />
              <div className="text-[11px] tracking-[0.22em] uppercase font-semibold" style={{ color: '#B7901F' }}>
                CoachDIA · Prism
              </div>
            </div>
            <div
              className="font-serif text-xl sm:text-[1.45rem] leading-snug"
              style={{ color: '#1C1147', fontWeight: 600, letterSpacing: '-0.01em' }}
            >
              Votre profil comportemental — archétype, forces, angles morts, plan 30/60/90
            </div>
            <div className="text-sm mt-1.5" style={{ color: '#5A5169' }}>
              Test de 12 min · rapport PDF premium · coachs humains recommandés · partage LinkedIn
            </div>
          </div>
          <div className="flex gap-2.5 flex-wrap relative">
            <button
              type="button"
              data-testid="dashboard-discovery-cta-profile"
              onClick={() => navigate('/my-discovery')}
              className="group inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold text-sm transition-all"
              style={{
                background: 'linear-gradient(135deg, #B7901F 0%, #D4AF37 50%, #F3D060 100%)',
                color: '#1C1147',
                border: '1px solid rgba(255,255,255,0.4)',
                boxShadow: '0 1px 0 rgba(255,255,255,0.4) inset, 0 12px 28px -10px rgba(212,175,55,0.55)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 1px 0 rgba(255,255,255,0.4) inset, 0 18px 38px -12px rgba(212,175,55,0.7)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 1px 0 rgba(255,255,255,0.4) inset, 0 12px 28px -10px rgba(212,175,55,0.55)';
              }}
            >
              Faire mon profil Prism
            </button>
            <button
              type="button"
              data-testid="dashboard-discovery-cta-team"
              onClick={() => navigate('/team-pulse/teams')}
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold text-sm transition-all"
              style={{
                background: '#FFFFFF',
                color: '#1C1147',
                border: '1.5px solid rgba(212,175,55,0.45)',
                boxShadow: '0 6px 16px -10px rgba(28,17,71,0.18)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#FFFCEC';
                e.currentTarget.style.borderColor = '#D4AF37';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#FFFFFF';
                e.currentTarget.style.borderColor = 'rgba(212,175,55,0.45)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Analyser mon équipe
            </button>
          </div>
        </div>

        {/* Tabs Navigation — PREMIUM (ivory/gold/violet · pills · scroll mobile) */}
        {(() => {
          const TAB_LIST = [
            { id: 'compte', label: 'Mon compte', icon: Settings, testId: 'tab-compte-btn' },
            { id: 'pedagogique', label: 'Pédagogie', icon: GraduationCap, testId: 'tab-pedagogie-btn' },
            { id: 'factures', label: 'Factures', icon: ReceiptIcon, testId: 'tab-factures-btn' },
            { id: 'mon-contexte', label: 'Mon contexte', icon: Shield, testId: 'tab-mon-contexte-btn' },
            ...(userCompanies.length > 0
              ? [{ id: 'entreprise', label: 'Entreprise', icon: Building2, testId: 'enterprise-tab-btn', badge: userCompanies.length }]
              : []),
            ...(data?.user?.role === 'affiliate'
              ? [{ id: 'affiliation', label: 'Dashboard Affilié', icon: Users, testId: 'tab-affiliation-btn' }]
              : []),
          ];
          return (
            <div className="mt-6">
              <div
                data-testid="user-dashboard-tabs"
                className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0"
                style={{ scrollbarWidth: 'thin', WebkitOverflowScrolling: 'touch' }}
              >
                {TAB_LIST.map((t) => {
                  const Icon = t.icon;
                  const active = activeTab === t.id;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setActiveTab(t.id)}
                      data-testid={t.testId}
                      className="relative flex-shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-semibold whitespace-nowrap transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
                      style={
                        active
                          ? {
                              background: 'linear-gradient(135deg, #FFFCEC 0%, #FFFFFF 60%, #F7F2E2 100%)',
                              color: '#1C1147',
                              border: '1.5px solid #D4AF37',
                              boxShadow:
                                '0 8px 22px -10px rgba(212,175,55,0.55), inset 0 1px 0 rgba(255,255,255,0.7)',
                            }
                          : {
                              background: 'rgba(255,255,255,0.6)',
                              color: '#5A5169',
                              border: '1px solid rgba(212,175,55,0.18)',
                              backdropFilter: 'blur(8px)',
                            }
                      }
                    >
                      <Icon className="w-4 h-4" style={{ color: active ? '#B7901F' : '#8E84A0' }} />
                      {t.label}
                      {t.badge != null && (
                        <span
                          className="ml-1 px-1.5 py-0.5 text-[10px] rounded-full font-bold"
                          style={{
                            background: active ? '#1C1147' : 'rgba(28,17,71,0.08)',
                            color: active ? '#F3D060' : '#1C1147',
                          }}
                        >
                          {t.badge}
                        </span>
                      )}
                      {active && (
                        <motion.span
                          layoutId="activeTabHalo"
                          className="absolute inset-0 rounded-full pointer-events-none"
                          style={{
                            boxShadow: '0 0 0 3px rgba(212,175,55,0.12)',
                          }}
                          transition={{ type: 'spring', stiffness: 400, damping: 35 }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
              {/* Subtle gold separator under the tab strip */}
              <div
                className="h-px w-full mt-1"
                style={{
                  background:
                    'linear-gradient(90deg, transparent 0%, rgba(212,175,55,0.4) 30%, rgba(212,175,55,0.4) 70%, transparent 100%)',
                }}
              />
            </div>
          );
        })()}
      </motion.div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto">
        {/* ========================================= */}
        {/* 🔧 ONGLET: MON COMPTE */}
        {/* CONTIENT UNIQUEMENT: Infos personnelles, */}
        {/* abonnement, mot de passe */}
        {/* AUCUNE DONNÉE PÉDAGOGIQUE */}
        {/* ========================================= */}
        {activeTab === 'compte' && (
          <div className="space-y-8 pb-20 md:pb-4">
            {/* 🎫 WIDGET SESSIONS PREMIUM (Phase 2 MOA) */}
            {/* Visible uniquement pour B2C (showQuota=true) */}
            {sessionCredits.showQuota && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.05 }}
              >
                <SessionsWidget 
                  onRechargeClick={() => setShowRechargeModal(true)}
                />
              </motion.div>
            )}
            
            {/* ═══════════════════════════════════════════════════════════════════
                P1 MOA FINAL (22 Jan 2026): CTA PRINCIPAL - STRIPE DIRECT
                - UN SEUL CLIC → Stripe Billing Portal
                - AUCUN pop-up intermédiaire
                ═══════════════════════════════════════════════════════════════════ */}
            {(data?.subscription?.status === 'trialing' || data?.subscription?.status === 'active') && 
             data?.subscription?.stripe_subscription_id && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.1 }}
                className="flex flex-col gap-3"
              >
                <Button 
                  onClick={() => setShowPackModal(true)}
                  data-testid="main-change-plan-btn"
                  className="w-full md:w-auto md:mx-auto px-8 py-4 bg-gradient-to-r from-purple-600 via-pink-500 to-purple-600 text-white text-lg font-semibold rounded-2xl hover:from-purple-700 hover:via-pink-600 hover:to-purple-700 shadow-lg hover:shadow-xl transition-all"
                >
                  <TrendingUp className="w-5 h-5 mr-2" />
                  Voir les options de pack
                </Button>
              </motion.div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Account & Subscription Section */}
            <motion.div 
              initial={{ y: 50, opacity: 0 }} 
              animate={{ y: 0, opacity: 1 }} 
              transition={{ delay: 0.1 }}
            >
              <Card className="rounded-3xl shadow-xl bg-white/80 backdrop-blur-md border border-white/30 hover:shadow-2xl transition-all h-full">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-2xl text-purple-700">
                    <User className="h-6 w-6" />
                    Mon Compte
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-500">Nom :</span>
                      <span className="text-base font-semibold text-gray-800">{data.user.name}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-500">Email :</span>
                      <span className="text-base text-gray-700">{data.user.email}</span>
                    </div>
                    <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                      <CreditCard className="h-4 w-4 text-purple-500" />
                      <span className="text-sm font-medium text-gray-500">Plan :</span>
                      <span className="text-base font-bold text-purple-600">
                        {/* P0: Afficher le niveau d'abonnement réel (Découverte/Standard/Pro) */}
                        {data.subscription.subscription_level 
                          ? data.subscription.subscription_level 
                          : data.subscription.plan === 'premium' 
                            ? 'Premium' 
                            : data.subscription.plan === 'free' 
                              ? 'Gratuit' 
                              : data.subscription.plan}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-500">Abonnement :</span>
                      <span className={`text-base font-bold ${getStatusColor(data.subscription.status)}`}>
                        {getStatusLabel(data.subscription.status)}
                      </span>
                    </div>
                    
                    {/* Trial end date */}
                    {data.subscription.status === 'trialing' && data.subscription.trial_end && (
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-blue-500" />
                          <span className="text-sm text-blue-700">
                            ✨ Essai gratuit se termine le : <strong>{new Date(data.subscription.trial_end).toLocaleDateString('fr-FR')}</strong>
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {/* Next payment date if active */}
                    {data.subscription.status === 'active' && data.subscription.current_period_end && (
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-green-500" />
                        <span className="text-sm font-medium text-gray-500">Prochain renouvellement :</span>
                        <span className="text-base font-semibold text-green-600">
                          {new Date(data.subscription.current_period_end).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                    )}
                    
                    {/* Access until date if canceled_pending */}
                    {data.subscription.status === 'canceled_pending' && data.subscription.subscription_end_date && (
                      <div className="p-3 bg-orange-50 border border-orange-200 rounded-xl">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-orange-500" />
                          <span className="text-sm text-orange-700">
                            ⚠️ Accès premium jusqu&apos;au : <strong>{new Date(data.subscription.subscription_end_date).toLocaleDateString('fr-FR')}</strong>
                          </span>
                        </div>
                      </div>
                    )}
                    
                    {/* P1 MOA (21 Jan 2026): Downgrade pending notification */}
                    {data.subscription.downgrade_pending && data.subscription.downgrade_to_pack_name && (
                      <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
                        <div className="flex items-center gap-2">
                          <TrendingDown className="h-4 w-4 text-amber-500" />
                          <span className="text-sm text-amber-700">
                            📦 Passage au pack <strong>{data.subscription.downgrade_to_pack_name}</strong> prévu à la prochaine échéance
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* ═══════════════════════════════════════════════════════════════════
                      P1 MOA (22 Jan 2026): HIÉRARCHIE UX ANTI-CHURN
                      1. Bouton Upgrade/Changer de pack = PRIMAIRE (gradient, visible)
                      2. Annulation = SECONDAIRE (lien discret)
                      ═══════════════════════════════════════════════════════════════════ */}
                  
                  {/* P1 MOA FINAL: Le bouton principal est maintenant au-dessus, sous SessionsWidget
                      Ici on garde UNIQUEMENT le lien discret de résiliation */}

                  {/* Annulation: SECONDAIRE - lien discret en bas de carte */}
                  {(data.subscription.status === 'trialing' || data.subscription.status === 'active') && 
                   data.subscription.stripe_subscription_id && (
                    <button 
                      onClick={handleCancelSubscription} 
                      data-testid="cancel-subscription-btn"
                      className="w-full text-center text-xs text-gray-400 hover:text-gray-500 underline transition-colors py-1 mt-2"
                      disabled={loadingCancel}
                    >
                      {loadingCancel ? "Annulation en cours..." : "Résilier mon abonnement"}
                    </button>
                  )}
                  
                  {/* Manual premium info */}
                  {data.subscription.status === 'manual' && 
                   data.subscription.plan === 'premium' && 
                   data.subscription.source === 'manual' && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl">
                      <div className="flex items-center gap-2">
                        <Info className="h-4 w-4 text-blue-500" />
                        <span className="text-sm text-blue-700">
                          Accès premium (gestion manuelle)
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </motion.div>

            {/* Password Change Section - Separate Card */}
            <motion.div 
              initial={{ y: 50, opacity: 0 }} 
              animate={{ y: 0, opacity: 1 }} 
              transition={{ delay: 0.2 }}
            >
              <Card className="rounded-3xl shadow-xl bg-white/80 backdrop-blur-md border border-white/30 hover:shadow-2xl transition-all h-full">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-2xl text-purple-700">
                    <Lock className="h-6 w-6" />
                    Sécurité
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <h3 className="font-semibold mb-4 text-purple-600 flex items-center gap-2">
                      <Lock className="h-4 w-4" />
                      Changer mon mot de passe
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <Label htmlFor="old-password" className="text-sm text-gray-600">Ancien mot de passe</Label>
                        <Input
                          id="old-password"
                          type="password"
                          placeholder="••••••••"
                          className="w-full p-3 rounded-xl bg-white/60 border border-purple-200 focus:border-purple-400"
                          value={passwords.old}
                          onChange={(e) => setPasswords({ ...passwords, old: e.target.value })}
                        />
                      </div>
                      <div>
                        <Label htmlFor="new-password" className="text-sm text-gray-600">Nouveau mot de passe</Label>
                        <Input
                          id="new-password"
                          type="password"
                          placeholder="••••••••"
                          className="w-full p-3 rounded-xl bg-white/60 border border-purple-200 focus:border-purple-400"
                          value={passwords.new}
                          onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                        />
                      </div>
                      <Button 
                        onClick={handlePasswordChange} 
                        className="w-full bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-xl hover:from-purple-700 hover:to-pink-600"
                        disabled={loadingPassword}
                      >
                        {loadingPassword ? "Mise à jour..." : "🔐 Mettre à jour"}
                      </Button>
                    </div>
                  </div>
                  
                  {/* Info pour rediriger vers Pédagogie */}
                  <div className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                    <div className="flex items-start gap-3">
                      <GraduationCap className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-purple-700">
                          Consultez votre progression pédagogique
                        </p>
                        <p className="text-xs text-gray-600 mt-1">
                          Vos objectifs, modules et certifications sont disponibles dans l&apos;onglet <strong>Pédagogie</strong>.
                        </p>
                        <Button
                          variant="link"
                          className="p-0 h-auto text-purple-600 text-sm mt-2"
                          onClick={() => setActiveTab('pedagogique')}
                        >
                          Voir ma progression →
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
          </div>
        )}

        {/* 🎫 Modal Sessions Épuisées (Phase 2 MOA) */}
        <SessionsExhaustedModal
          isOpen={showRechargeModal}
          onClose={() => setShowRechargeModal(false)}
          onSelectPack={(packId) => {
            // Phase 4: Redirection vers Stripe
            console.log('Pack selected:', packId);
            setShowRechargeModal(false);
          }}
        />

        {/* Dashboard Affilié Tab */}
        {activeTab === 'affiliation' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full pb-20 md:pb-4"
          >
            <AffiliateDashboardAwwwards embedded={true} />
          </motion.div>
        )}

        {/* ========================================= */}
        {/* 🎓 ONGLET: PÉDAGOGIE */}
        {/* UNIQUE SOURCE DE VÉRITÉ pour: */}
        {/* - % progression globale */}
        {/* - Modules terminés/en cours */}
        {/* - Objectifs validés */}
        {/* - Preuves collectées */}
        {/* - Diagnostics */}
        {/* - Certificats */}
        {/* ========================================= */}
        {activeTab === 'pedagogique' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full pb-20 md:pb-4"
          >
            <PedagogicalSummary />
          </motion.div>
        )}

        {/* ========================================= */}
        {/* 🏢 ONGLET: ENTREPRISE */}
        {/* Gestion des licences B2B */}
        {/* Visible uniquement pour admins entreprise */}
        {/* ========================================= */}
        {activeTab === 'entreprise' && userCompanies.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full pb-20 md:pb-4"
            data-testid="enterprise-tab-content"
          >
            <EnterpriseLicenseTab companies={userCompanies} />
          </motion.div>
        )}

        {/* ========================================= */}
        {/* 🧾 ONGLET: FACTURES (Phase 2) */}
        {/* Liste B2C facturée par AMSY E-COM SAS */}
        {/* Accessible 10 ans, accordéon par année */}
        {/* ========================================= */}
        {activeTab === 'factures' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full pb-20 md:pb-4"
          >
            <InvoicesTab />
          </motion.div>
        )}

        {/* ========================================= */}
        {/* 🔒 ONGLET: MON CONTEXTE (Owen → Coach humain) */}
        {/* RGPD strict · consentements + synthèse + révocation */}
        {/* ========================================= */}
        {activeTab === 'mon-contexte' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full pb-20 md:pb-4"
            data-testid="mon-contexte-tab-content"
          >
            <MyContextSharesTab />
          </motion.div>
        )}
      </div>
      
      {/* ═══════════════════════════════════════════════════════════════════════════
          P1 MOA: MODALE SÉLECTION DES PACKS AVEC PRIX
          Affiche les 3 packs (Découverte, Standard, Pro) avec prix
          Clic sur pack → Redirection vers Stripe Checkout
          ═══════════════════════════════════════════════════════════════════════════ */}
      {showPackModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full p-6"
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-gray-800">Choisir votre pack</h2>
              <button 
                onClick={() => setShowPackModal(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                data-testid="close-pack-modal"
              >
                ✕
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {PACKS_CONFIG.map((pack) => {
                const isCurrentPack = data?.subscription?.subscription_pack_id === pack.id;
                return (
                  <button
                    key={pack.id}
                    data-testid={`pack-${pack.id}`}
                    disabled={loadingPlanChange || isCurrentPack}
                    onClick={() => handleSelectPack(pack.id)}
                    className={`relative p-5 rounded-xl border-2 text-left transition-all ${
                      isCurrentPack 
                        ? 'border-purple-500 bg-purple-50 cursor-default' 
                        : pack.popular 
                          ? 'border-pink-400 bg-gradient-to-b from-pink-50 to-white hover:border-pink-500 hover:shadow-lg cursor-pointer'
                          : 'border-gray-200 hover:border-purple-400 hover:shadow-md cursor-pointer'
                    } ${loadingPlanChange ? 'opacity-50' : ''}`}
                  >
                    {pack.popular && !isCurrentPack && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-pink-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                        POPULAIRE
                      </span>
                    )}
                    {isCurrentPack && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full">
                        ACTUEL
                      </span>
                    )}
                    <div className="text-center">
                      <h3 className="text-lg font-bold text-gray-800 mb-1">{pack.name}</h3>
                      <div className="mb-3">
                        <span className="text-3xl font-bold text-purple-700">{pack.price}€</span>
                        <span className="text-gray-500 text-sm">/mois</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">
                        <span className="font-semibold text-purple-600">{pack.sessions}</span> crédits/mois
                      </p>
                      {!isCurrentPack && (() => {
                        const currentIdx = PACKS_CONFIG.findIndex(p => p.id === data?.subscription?.subscription_pack_id);
                        const packIdx = PACKS_CONFIG.findIndex(p => p.id === pack.id);
                        const isUpgrade = packIdx > currentIdx;
                        return (
                          <span className={`inline-block text-xs font-medium px-2 py-1 rounded-full ${
                            isUpgrade ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {isUpgrade ? '⬆ Upgrade' : '⬇ Downgrade'}
                          </span>
                        );
                      })()}
                    </div>
                  </button>
                );
              })}
            </div>
            
            {loadingPlanChange && (
              <div className="flex items-center justify-center gap-3 mt-6 text-purple-600">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm font-medium">Redirection vers Stripe...</span>
              </div>
            )}
          </motion.div>
        </div>
      )}

      {/* 🧭 Modales d'aide — desktop (lg+) vs mobile */}
      <div className="hidden lg:block">
        <HelpDesktopModal
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          userContext={{ role: 'client', isAffiliate: false, isB2BAdmin: false }}
        />
      </div>
      <div className="lg:hidden">
        <HelpMobileSheet
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          userContext={{ role: 'client', isAffiliate: false, isB2BAdmin: false }}
        />
      </div>
    </div>
  );
}
